import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShareStory = ({ userId }) => {
  const navigate = useNavigate();
  const [story, setStory] = useState({
    title: '',
    content: '',
    category: 'Safe Nightlife',
    author: 'Anonymous'
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!story.title || !story.content) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/stories`, story);
      toast.success('Your story has been shared!', {
        description: 'Thank you for contributing to the community'
      });
      navigate('/user-stories');
    } catch (error) {
      console.error('Error sharing story:', error);
      toast.error('Failed to share story. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen app-container p-6 md:p-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 flex items-center gap-4">
          <Button
            onClick={() => navigate('/user-stories')}
            variant="ghost"
            className="rounded-full"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-3xl md:text-5xl font-extrabold text-[#1A1A1A] dark:text-[#FFFFFE]">
            Share Your Story
          </h1>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-[#232129] rounded-3xl p-8"
        >
          <p className="text-[#5F6C7B] dark:text-[#94A1B2] mb-6 leading-relaxed">
            Your experiences can help others. Share your story anonymously to inspire, educate, or support someone going through something similar. All stories are reviewed before being published.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-sm font-medium mb-2 block text-[#1A1A1A] dark:text-[#FFFFFE]">
                Story Title
              </label>
              <Input
                value={story.title}
                onChange={(e) => setStory({ ...story, title: e.target.value })}
                placeholder="Give your story a title..."
                className="bg-gray-50 dark:bg-gray-900 border-2 border-transparent focus:border-[#CCFF00] rounded-xl py-6 text-lg"
                data-testid="story-title-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block text-[#1A1A1A] dark:text-[#FFFFFE]">
                Category
              </label>
              <select
                value={story.category}
                onChange={(e) => setStory({ ...story, category: e.target.value })}
                className="w-full bg-gray-50 dark:bg-gray-900 border-2 border-transparent focus:border-[#CCFF00] rounded-xl px-4 py-3 outline-none"
                data-testid="story-category-select"
              >
                <option>Safe Nightlife</option>
                <option>Mental Health & Substance Use</option>
                <option>Overdose Prevention</option>
                <option>Communication</option>
                <option>General Experience</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block text-[#1A1A1A] dark:text-[#FFFFFE]">
                Your Story
              </label>
              <Textarea
                value={story.content}
                onChange={(e) => setStory({ ...story, content: e.target.value })}
                placeholder="Share your experience, what you learned, or advice for others..."
                rows={12}
                className="bg-gray-50 dark:bg-gray-900 border-2 border-transparent focus:border-[#CCFF00] rounded-xl text-lg"
                data-testid="story-content-textarea"
              />
              <p className="text-sm text-[#5F6C7B] dark:text-[#94A1B2] mt-2">
                Share as much or as little as you're comfortable with
              </p>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block text-[#1A1A1A] dark:text-[#FFFFFE]">
                Display Name (Optional)
              </label>
              <Input
                value={story.author}
                onChange={(e) => setStory({ ...story, author: e.target.value || 'Anonymous' })}
                placeholder="Anonymous"
                className="bg-gray-50 dark:bg-gray-900 border-2 border-transparent focus:border-[#CCFF00] rounded-xl"
                data-testid="story-author-input"
              />
              <p className="text-sm text-[#5F6C7B] dark:text-[#94A1B2] mt-2">
                Leave blank to post anonymously
              </p>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#CCFF00] text-black font-bold px-8 py-4 rounded-full hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-story-button"
            >
              <Send className="w-5 h-5 mr-2" />
              {submitting ? 'Sharing...' : 'Share Story'}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default ShareStory;
